import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { placeProxyBid, awaitBidResolution, bidResolutionMessage } from '../../services/bidding.service';
import { resolutionToOutcome, describeOutcome } from '../../services/bidOutcome';
import toast from 'react-hot-toast';

interface ProxyBidModalProps {
  auctionId: string;
  lotId: string;
  lotNumber: string;
  lotTitle: string;
  currentBid: number; // In cents
  nextMinBid: number; // In cents
  onClose: () => void;
  onPlacing?: () => void;     // Fired right BEFORE the placement await (starts BidPanel settling)
  onPlaceFailed?: () => void; // Fired if the placement is rejected (clears BidPanel settling)
}

export const ProxyBidModal: React.FC<ProxyBidModalProps> = ({
  auctionId,
  lotId,
  lotNumber,
  lotTitle,
  currentBid,
  nextMinBid,
  onClose,
  onPlacing,
  onPlaceFailed,
}) => {
  const { currentUser, userProfile } = useAuth();
  const [maxBid, setMaxBid] = useState('');
  const [placing, setPlacing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentUser || !userProfile) {
      toast.error('Please sign in to place a proxy bid');
      return;
    }

    const maxBidAmount = parseFloat(maxBid);
    if (isNaN(maxBidAmount) || maxBidAmount <= 0) {
      toast.error('Please enter a valid maximum bid');
      return;
    }

    const maxBidInCents = Math.round(maxBidAmount * 100);

    if (maxBidInCents < nextMinBid) {
      toast.error(`Maximum bid must be at least $${(nextMinBid / 100).toFixed(2)}`);
      return;
    }

    setPlacing(true);
    // Start BidPanel's settling BEFORE the await (after client-side validation above), so the
    // "Confirming bid…" badge is gated from the first frame — no green "Winning!" flash.
    onPlacing?.();

    try {
      const result = await placeProxyBid(
        auctionId,
        lotId,
        maxBidInCents,
        currentUser.uid,
        userProfile.displayName || 'Anonymous'
      );

      if (result.success) {
        // The proxy is saved, but its OPENING bid was written 'pending'. Keep "Setting..." until
        // the server grants or refuses it — never announce the lead before then. No auto-retry,
        // no second write.
        const resolution = result.openingBidId
          ? await awaitBidResolution(result.openingBidId)
          : ({ kind: 'winning' } as const); // no opening bid written (already leading; max raised)
        if (resolution.kind === 'winning') {
          toast.success(`Proxy bid set! Max: $${(maxBidInCents / 100).toFixed(2)}`);
          onPlacing?.(); // restart BidPanel's settle window from the grant
          onClose();
        } else if (resolution.kind === 'timeout') {
          toast(`Proxy saved (max $${(maxBidInCents / 100).toFixed(2)}). ${bidResolutionMessage(resolution)}`,
            { icon: '⏳', duration: 8000 });
          onPlaceFailed?.();
          onClose();
        } else {
          // The proxy itself stays active at its max; only its opening bid was not granted. Say
          // why in the server's terms (a 'closed' refusal carries the server-held close time).
          const o = await resolutionToOutcome(auctionId, lotId, result.openingBidId as string, resolution);
          const t = describeOutcome(o);
          toast.error(`Proxy saved (max $${(maxBidInCents / 100).toFixed(2)}), but its opening bid was not placed. ` +
            `${t.title}. ${t.text}`, { duration: 12000 });
          onPlaceFailed?.();
          onClose();
        }
      } else {
        toast.error(result.error || 'Failed to set proxy bid');
        onPlaceFailed?.();
      }
    } catch (error) {
      console.error('Proxy bid error:', error);
      toast.error('Failed to set proxy bid');
      onPlaceFailed?.();
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">Set Proxy Bid</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-600">LOT {lotNumber}</p>
          <p className="font-bold">{lotTitle}</p>
          <p className="text-sm text-gray-600 mt-2">
            Current Bid: <span className="font-bold text-primary-600">${(currentBid / 100).toFixed(2)}</span>
          </p>
        </div>

        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>How Proxy Bidding Works:</strong>
          </p>
          <ul className="text-sm text-blue-700 mt-2 space-y-1 ml-4 list-disc">
            <li>Set your maximum bid amount</li>
            <li>We'll automatically bid for you when you're outbid</li>
            <li>We'll never bid more than your maximum</li>
            <li>You'll be notified if you're outbid above your max</li>
          </ul>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Your Maximum Bid *
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
              <input
                type="number"
                step="0.01"
                min={(nextMinBid / 100).toFixed(2)}
                value={maxBid}
                onChange={(e) => setMaxBid(e.target.value)}
                placeholder={(nextMinBid / 100).toFixed(2)}
                className="input pl-8 text-lg"
                required
                disabled={placing}
                autoFocus
              />
            </div>
            <p className="text-xs text-gray-600 mt-1">
              Minimum: ${(nextMinBid / 100).toFixed(2)}
            </p>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 btn-secondary"
              disabled={placing}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 btn-primary"
              disabled={placing || !maxBid}
            >
              {placing ? 'Setting...' : 'Set Proxy Bid'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};